<template>
  <div class="rm-screen">
    <report-meals-packages-add-meal
      v-if="selectedCompany"
      v-model="addMealDialog"
      :company="selectedCompany"
      @refreshMeal="loadMealsForRestaurantCompanies"
    ></report-meals-packages-add-meal>

    <div v-if="!selectedCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="isLoading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!packingCompanies.length" class="rm-empty">
      {{
        filters.search
          ? $t("report_mobile.no_results_for", { search: filters.search })
          : $t("report_mobile.no_meals")
      }}
    </div>

    <template v-else>
      <p class="rm-muted rm-pack-note">{{ $t("report_mobile.packing_local_note") }}</p>

      <article
        v-for="company in packingCompanies"
        :key="company.key"
        class="rm-card rm-pack"
        :class="{ 'is-open': company.key === openKey }"
      >
        <button
          type="button"
          class="rm-pack__head"
          :aria-expanded="String(company.key === openKey)"
          @click="toggleOpen(company.key)"
        >
          <span class="rm-pack__title">
            <span class="rm-pack__name">{{ company.name }}</span>
            <span class="rm-muted">{{ companySubtitle(company) }}</span>
          </span>
          <span
            class="rm-pack__progress-text rm-num"
            :class="{ 'is-complete': company.complete }"
          >
            <v-icon v-if="company.ready" size="18" color="#15803d">mdi-check-circle</v-icon>
            {{ company.done }} / {{ company.total }}
          </span>
          <v-icon
            size="18"
            color="#8a817a"
            class="rm-pack__chevron"
            :class="{ 'is-open': company.key === openKey }"
          >
            mdi-chevron-down
          </v-icon>
        </button>

        <div v-if="company.key === openKey || company.done > 0" class="rm-pack__bar">
          <div
            class="rm-progress"
            :class="{ 'rm-progress--complete': company.complete }"
            role="progressbar"
            :aria-valuenow="company.done"
            aria-valuemin="0"
            :aria-valuemax="company.total"
          >
            <div class="rm-progress__fill" :style="{ width: `${company.percent}%` }"></div>
          </div>
        </div>

        <template v-if="company.key === openKey">
          <ul class="rm-list rm-pack__items">
            <li v-for="meal in company.meals" :key="meal.id">
              <label class="rm-pack__item">
                <input
                  type="checkbox"
                  :checked="meal.packed"
                  @change="togglePacked(company.key, meal.id)"
                />
                <span class="rm-pack__item-text">
                  <span class="rm-pack__item-name" :class="{ 'is-done': meal.packed }">
                    {{ meal.name }}
                  </span>
                  <span v-if="meal.badges.length" class="rm-badges">
                    <span v-for="badge in meal.badges" :key="badge" class="rm-badge">
                      {{ badge }}
                    </span>
                  </span>
                </span>
                <span class="rm-pack__item-count rm-num" :class="{ 'is-done': meal.packed }">
                  {{ meal.count }}
                </span>
              </label>
            </li>
          </ul>

          <div class="rm-pack__actions">
            <button
              v-if="isSingleDay"
              type="button"
              class="rm-btn rm-btn--sm"
              @click="openAddDialog(company.source)"
            >
              <v-icon size="18" color="#1c1917">mdi-plus</v-icon>
              {{ $t("report_mobile.add_meal") }}
            </button>
            <button
              v-if="company.ready"
              type="button"
              class="rm-btn rm-btn--success"
              @click="setReady(company.key, false)"
            >
              <v-icon size="20" color="#ffffff">mdi-check</v-icon>
              {{ $t("report_mobile.company_ready") }}
            </button>
            <button
              v-else-if="company.complete"
              type="button"
              class="rm-btn rm-btn--primary"
              @click="setReady(company.key, true)"
            >
              {{ $t("report_mobile.mark_company_ready") }}
            </button>
            <button v-else type="button" class="rm-btn rm-btn--muted" disabled>
              {{ $t("report_mobile.pack_remaining", { n: company.total - company.done }) }}
            </button>
          </div>
        </template>
      </article>
    </template>
  </div>
</template>

<script>
import ReportMealsTabPackages from "@/components/report/meals/tabs/ReportMealsTabPackages.vue";
import { SHIFTS, getShiftKey } from "@/constants/shifts";
import hasPlural from "../hasPlural";

const STORAGE_PREFIX = "rm-packing";

/**
 * Mobilni prikaz "Pakovanje": nasleđuje učitavanje, dodavanje obroka, štampu i Excel
 * iz ReportMealsTabPackages. Stanje čekliste ("spakovano") čuva se samo na ovom uređaju.
 */
export default {
  name: "ReportMobilePackages",
  extends: ReportMealsTabPackages,
  mixins: [hasPlural],
  props: {
    shifts: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    packingState: {},
    /** undefined = automatski (prva nespremna firma), null = sve zatvoreno */
    expandedKey: undefined,
  }),
  computed: {
    isSingleDay() {
      return this.filters.dates.from === this.filters.dates.to;
    },
    storageKey() {
      const restaurantId = this.$store.state.restaurant?.id;
      const { from, to } = this.filters.dates;
      return [STORAGE_PREFIX, restaurantId, from, to, this.shifts.join("")].join(":");
    },
    packingCompanies() {
      const search = this.filters.search;
      return this.companies
        .map((company) => this.toPackingCompany(company, search))
        .filter((company) => company.total > 0);
    },
    openKey() {
      if (this.expandedKey !== undefined) {
        return this.expandedKey;
      }
      const firstPending = this.packingCompanies.find((company) => !company.ready);
      return firstPending ? firstPending.key : null;
    },
  },
  watch: {
    shifts: {
      handler(shifts) {
        this.selectedShifts = [...shifts];
      },
      immediate: true,
    },
    storageKey: {
      handler() {
        this.packingState = this.readPackingState();
        this.expandedKey = undefined;
      },
      immediate: true,
    },
  },
  methods: {
    toPackingCompany(company, search) {
      const key = company.name;
      const state = this.packingState[key] || { meals: {}, ready: false };
      const companyMatches = !search || this.matches(company.name, search);

      const meals = (company.meals || [])
        .filter((meal) => companyMatches || this.matches(meal.name, search))
        .map((meal) => {
          const perShift = this.selectedShifts
            .map((shiftId) => ({
              shiftId,
              quantity: meal[getShiftKey(shiftId)]?.totalQuantity || 0,
              restaurantQuantity: meal[getShiftKey(shiftId)]?.restaurantQuantity || 0,
            }))
            .filter((shift) => shift.quantity > 0);
          const count = perShift.reduce((sum, shift) => sum + shift.quantity, 0);
          const restaurantCount = perShift.reduce(
            (sum, shift) => sum + shift.restaurantQuantity,
            0
          );
          const badges =
            restaurantCount > 0
              ? [this.$t("report_mobile.restaurant_ordered", { n: restaurantCount })]
              : [];
          return {
            id: meal.id,
            name: meal.name,
            count,
            badges,
            shiftIds: perShift.map((shift) => shift.shiftId),
            packed: Boolean(state.meals[meal.id]),
          };
        })
        .filter((meal) => meal.count > 0);

      const total = meals.reduce((sum, meal) => sum + meal.count, 0);
      const done = meals
        .filter((meal) => meal.packed)
        .reduce((sum, meal) => sum + meal.count, 0);
      const complete = total > 0 && done === total;
      const shiftIds = SHIFTS.map((shift) => shift.id).filter((shiftId) =>
        meals.some((meal) => meal.shiftIds.includes(shiftId))
      );

      return {
        key,
        source: company,
        name: this.toLatin(company.name),
        meals,
        total,
        done,
        complete,
        ready: complete && Boolean(state.ready),
        percent: total ? Math.round((done / total) * 100) : 0,
        shiftIds,
      };
    },
    matches(text, search) {
      return this.customSearch([{ text }], [{ value: "text" }], search).length > 0;
    },
    companySubtitle(company) {
      const meals = this.pluralT("report_mobile.meals_n", company.total);
      if (company.key === this.openKey) {
        const shifts =
          company.shiftIds.length === 1
            ? this.$t("report_mobile.shift_n", { n: company.shiftIds[0] })
            : this.$t("report_mobile.shifts_list", { list: company.shiftIds.join(", ") });
        return `${shifts} · ${meals}`;
      }
      if (company.ready) {
        return `${meals} · ${this.$t("report_mobile.status_ready")}`;
      }
      if (company.done === 0) {
        return `${meals} · ${this.$t("report_mobile.status_not_started")}`;
      }
      return `${meals} · ${this.$t("report_mobile.status_in_progress")}`;
    },
    toggleOpen(key) {
      this.expandedKey = this.openKey === key ? null : key;
    },
    togglePacked(companyKey, mealId) {
      const state = this.packingState[companyKey] || { meals: {}, ready: false };
      const meals = { ...state.meals, [mealId]: !state.meals[mealId] };
      this.updatePackingState(companyKey, { meals, ready: state.ready && meals[mealId] });
    },
    setReady(companyKey, ready) {
      const state = this.packingState[companyKey] || { meals: {}, ready: false };
      this.updatePackingState(companyKey, { ...state, ready });
      if (ready) {
        this.expandedKey = undefined;
      }
    },
    updatePackingState(companyKey, companyState) {
      this.packingState = { ...this.packingState, [companyKey]: companyState };
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.packingState));
      } catch (error) {
        // Bez localStorage-a čeklista radi samo dok je ekran otvoren.
      }
    },
    readPackingState() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey)) || {};
      } catch (error) {
        return {};
      }
    },
  },
};
</script>

<style lang="scss">
.rm-pack-note {
  padding: 0 4px;
}

.rm-theme .rm-pack {
  display: flex;
  flex-direction: column;

  &__head {
    width: 100%;
    min-height: 68px;
    padding: 14px 12px 14px 16px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
    color: var(--rm-text);
  }

  &__title {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 17px;
    font-weight: 700;
  }

  &.is-open &__name {
    font-size: 18px;
  }

  &__progress-text {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 700;

    &.is-complete {
      color: var(--rm-success);
    }
  }

  &__chevron {
    transition: transform 0.15s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__bar {
    padding: 0 16px 12px;
  }

  &.is-open &__bar {
    border-bottom: 1px solid var(--rm-divider);
  }

  &__items li + li {
    border-top: 1px solid var(--rm-divider-soft);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 60px;
    padding: 8px 16px;
    cursor: pointer;

    input {
      width: 24px;
      height: 24px;
      margin: 0;
      flex-shrink: 0;
    }
  }

  &__item-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__item-name {
    font-size: 16px;
    font-weight: 500;

    &.is-done {
      color: var(--rm-muted);
      text-decoration: line-through;
    }
  }

  &__item-count {
    font-size: 22px;
    font-weight: 700;

    &.is-done {
      color: var(--rm-muted);
    }
  }

  &__actions {
    padding: 12px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-top: 1px solid var(--rm-divider-soft);
  }
}

.rm-theme .rm-btn--success {
  border: none;
  background: var(--rm-success);
  color: #ffffff;
}
</style>
